import classnames from 'classnames';

import { Game } from '~/types/game';
import { FP_DEFAULTS, VoteOutcome } from '~/services/freePlay/types';
import { useTransientVisible } from '~/hooks/freePlay';

import styles from './VoteResultBanner.module.css';

export type VoteResultBannerProps = {
    /** The settled vote to announce on the TV. */
    outcome: VoteOutcome;
    /** Resolved proposed game (for the accepted message). */
    proposedGame?: Game;
};

/**
 * Brief result banner shown on the TV once a vote settles (accepted / rejected / expired),
 * so the whole room sees the verdict — not just the proposer. Bilingual FR/EN, like the
 * attract screen (the TV is shared; per-passenger language lives on the phones). Self-hides
 * after FP_DEFAULTS.voteResultMs based on outcome freshness (see useTransientVisible).
 */
export function VoteResultBanner({ outcome, proposedGame }: VoteResultBannerProps) {
    const visible = useTransientVisible(outcome.at, FP_DEFAULTS.voteResultMs);
    if (!visible) return null;

    const game = proposedGame?.name ?? outcome.proposedGameID;
    /* 'expired' (deadline, no majority) is neutral — not a rejection. */
    let cls = styles.rejected;
    let icon = '✕';
    let fr = 'Changement refusé';
    let en = 'Change rejected';
    if (outcome.result === 'accepted') {
        cls = styles.accepted;
        icon = '✓';
        fr = `On passe à ${game} !`;
        en = `Switching to ${game}!`;
    } else if (outcome.result === 'expired') {
        cls = styles.expired;
        icon = '⏱';
        fr = 'Vote terminé — pas de majorité';
        en = 'Vote ended — no majority';
    }

    return (
        <div className={classnames(styles.container, cls)} role="status">
            <span className={styles.icon} aria-hidden>
                {icon}
            </span>
            <div className={styles.lines}>
                <span className={styles.primary}>{fr}</span>
                <span className={styles.secondary}>{en}</span>
            </div>
        </div>
    );
}
